import { PayloadAction } from '@reduxjs/toolkit';

import updateDefaultSizeMeasurementValues from 'editor/src/store/design/operation/updateDefaultSizeMeasurementValues';
import { SpreadGroup } from 'editor/src/store/design/types';
import applyLinkedToGroups from 'editor/src/store/variants/helpers/applyLinkedToGroups';
import getGroupVariationByProductUid from 'editor/src/store/variants/helpers/getGroupVariationByProductUid';
import getInitialExternalOptions from 'editor/src/store/variants/helpers/getInitialExternalOptions';
import getInitialMultiOptions from 'editor/src/store/variants/helpers/getInitialMultiOptions';
import getInitialPageCount from 'editor/src/store/variants/helpers/getInitialPageCount';
import getInitialSingleOptions from 'editor/src/store/variants/helpers/getInitialSingleOptions';
import getSelectedGroupKey from 'editor/src/store/variants/helpers/getSelectedGroupKey';
import {
  ProductVariation,
  VariantsState,
  Product,
  Configuration,
  ExternalProductControl,
  VariationProductControl,
  isVariationProductControl,
  isExternalProductControl,
  ExistingVariant,
  ProductControlSize,
  ResizableElement,
  VariationGroup,
  DesignOptionControl,
} from 'editor/src/store/variants/types';

export type ExternalProduct = Omit<Product, 'productControls' | 'externalProductControls' | 'unavailableProducts'> & {
  productControls: Array<VariationProductControl | ExternalProductControl>;
  designOptionControls?: DesignOptionControl[];
  unavailableProducts?: Product['unavailableProducts'];
};

export type ProductDataPayload = {
  product: ExternalProduct;
  defaultPageCount: number | undefined;
  defaultProductUids: string[];
  defaultVariation: ProductVariation | undefined;
  defaultVariationsInfo: { [productUid: string]: { linked: boolean } } | undefined;
  selectedGroupKey: string | undefined;
  configuration: Configuration;
  existingVariants?: ExistingVariant[];
  layoutPerProductUids?: { [productUid: string]: string };
  groupedSpreadsPerProductUids?: { [productUid: string]: SpreadGroup[] };
  resizableElementPerProductUids?: { [productUid: string]: ResizableElement };
  disableVariationControls?: boolean;
  disableVariationControlsDescription?: string;
};

const setProductDataReducer = (
  state: VariantsState,
  action: PayloadAction<{
    productData: ProductDataPayload;
    updatedVariants?: ExistingVariant[];
    groups?: VariationGroup[];
  }>,
) => {
  const { productData, updatedVariants, groups } = action.payload;
  const {
    product,
    defaultVariation,
    defaultPageCount,
    defaultProductUids,
    configuration,
    selectedGroupKey,
    defaultVariationsInfo,
    disableVariationControls,
    disableVariationControlsDescription,
    existingVariants = [],
    layoutPerProductUids = {},
    groupedSpreadsPerProductUids = {},
    resizableElementPerProductUids = {},
  } = productData;

  // happens when there are no variations.
  if (!defaultVariation || !groups || !updatedVariants) {
    return;
  }

  const { designOptionControls } = product;

  const sizeControl = product.productControls?.find((control) => control.key === 'product-size') as ProductControlSize;
  if (sizeControl) {
    updateDefaultSizeMeasurementValues(sizeControl);
  }

  const productControls = product.productControls.filter(isVariationProductControl);

  const splitControlProduct: Product = {
    ...product,
    productControls,
    externalProductControls: product.productControls.filter(isExternalProductControl),
    unavailableProducts: product.unavailableProducts ?? {},
  };

  state.product = splitControlProduct;
  if (designOptionControls) {
    state.designOptionsControl = designOptionControls;
    state.designOptionsEnabled = true;
    const [firstOption] = designOptionControls[0].options;
    state.selectedDesignOptionValue = firstOption.value;
  }

  if (!state.product.controlsUIOrder) {
    state.product.controlsUIOrder = product.productControls.map((control) => control.key);
  }

  state.configuration = configuration || {};
  state.layoutPerProductUids = layoutPerProductUids;
  state.resizableElementPerProductUids = resizableElementPerProductUids;
  state.groupedSpreadsPerProductUids = groupedSpreadsPerProductUids;
  state.disableVariationControls = disableVariationControls;
  state.disableVariationControlsDescription = disableVariationControlsDescription;
  state.replaceControlKeys = product.productControls
    .filter((control) => control.type === 'single' && control.behavior === 'replace')
    .map((control) => control.key);

  existingVariants.forEach((variant) => {
    const updatedVariant = updatedVariants.find((updatedVariant) => {
      if (variant.designData?.dimensions && updatedVariant.designData?.dimensions) {
        return (
          updatedVariant.productUid === variant.productUid &&
          updatedVariant.designData.dimensions.width === variant.designData.dimensions.width &&
          updatedVariant.designData.dimensions.height === variant.designData.dimensions.height
        );
      }
      return updatedVariant.productUid === variant.productUid;
    });
    if (!updatedVariant) {
      return;
    }
    // GPS-25623 — preserve the saved variant's designData when it carries
    // any media (artwork) on any spread. The updatedVariant payload comes
    // from the design-template resolver and is the canvas's blank starting
    // template — it does NOT carry the customer's saved artwork. The old
    // blanket Object.assign(variant, updatedVariant) was wiping
    // existingVariants[i].designData (which the host explicitly set to the
    // saved structure on Edit-Designs reopen) and replacing it with the
    // template. The result was a strip row whose variant looked correctly
    // labelled but whose canvas dropped to the empty-design placeholder
    // when the user clicked into it. Keep updatedVariant fields for the
    // non-design parts (linked, designOptions, dimensions metadata, etc.),
    // and ONLY overwrite designData when the saved one has no media.
    const savedSpreads = (
      variant.designData as
        | {
            spreads?: Array<{
              pages?: Array<{ groups?: { media?: unknown[] } }>;
            }>;
          }
        | undefined
    )?.spreads;
    const savedHasMedia = Array.isArray(savedSpreads)
      ? savedSpreads.some((spread) => (spread?.pages?.[0]?.groups?.media?.length ?? 0) > 0)
      : false;
    if (savedHasMedia) {
      const { designData: _ignored, ...rest } = updatedVariant;
      Object.assign(variant, rest);
    } else {
      Object.assign(variant, updatedVariant);
    }
  });

  state.isLoaded = true;
  state.selectedPageCount = getInitialPageCount(defaultPageCount, existingVariants);
  state.selectedMultiOptions = getInitialMultiOptions(
    productControls,
    product.productVariations,
    defaultProductUids,
    existingVariants,
    !!configuration.singleSelection,
  );
  state.selectedExternalOptions = getInitialExternalOptions(
    splitControlProduct.externalProductControls,
    existingVariants,
  );
  let singleControlVariation = defaultVariation;
  if (existingVariants.length) {
    singleControlVariation =
      product.productVariations.find((variation) => variation.productUid === existingVariants[0].productUid) ??
      defaultVariation;
  }
  state.selectedSingleOptions = getInitialSingleOptions(splitControlProduct.productControls, singleControlVariation);
  applyLinkedToGroups(groups, defaultVariationsInfo);

  state.variationGroups = groups;
  state.selectedGroupKey =
    selectedGroupKey ||
    (defaultVariation && getGroupVariationByProductUid(groups, defaultVariation.productUid))?.key ||
    getSelectedGroupKey(state.variationGroups, undefined);
};

export default setProductDataReducer;
